var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import classNames from 'classnames';
import { UXPContext } from '@core/context/Context';
import { blobToDataUrl, validateAllowedFileType } from './fileHelper';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { getInputSizeStyles, getInputStateClass } from '../utils';
/**
 * @export
 * FileInput component - Drag and drop file upload with preview
 * Follows v5 input pattern with theme support and accessibility
 */
const FileInput = React.forwardRef((props, ref) => {
    const context = React.useContext(UXPContext);
    let { value, onChange, allowedTypes, preview, className, dropAreaIcon, dropAreaLabel, minWidth, maxWidth, width, fullWidth, disabled, readOnly, isValid: isValidProp, compact, hidden } = props;
    if (!preview)
        preview = { showPreview: true, showName: false };
    if (preview.showPreview == undefined)
        preview.showPreview = true;
    if (preview.showName == undefined)
        preview.showName = false;
    const [previewDetails, setPreviewDetails] = React.useState(null);
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const [isDragging, setIsDragging] = React.useState(false);
    const [internalValidationError, setInternalValidationError] = React.useState(false);
    const inputRef = React.useRef(null);
    const containerRef = React.useRef(null);
    // Filter the OS file picker to the allowed set (MIME types and extensions both work here)
    const acceptAttr = (allowedTypes === null || allowedTypes === void 0 ? void 0 : allowedTypes.length) ? allowedTypes.join(',') : undefined;
    React.useImperativeHandle(ref, () => ({
        open: () => {
            var _a;
            if (!disabled && !readOnly)
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
        },
    }), [disabled, readOnly]);
    React.useEffect(() => {
        processValue();
    }, [value]);
    React.useEffect(() => {
    }, [previewDetails]);
    function processValue() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!value) {
                setPreviewDetails(null);
                return;
            }
            let file = null;
            if (typeof value == 'string') {
                try {
                    file = yield fetch(value).then(r => r.blob());
                }
                catch (e) {
                    console.log('Unable to fetch the file ', e);
                }
            }
            else {
                file = value;
            }
            if (!file) {
                setPreviewDetails({
                    isValid: undefined,
                    type: 'unknown',
                    mime: 'unknown',
                    name: value,
                    dataUrl: value
                });
                return;
            }
            const isAllowed = validateAllowedFileType(file.type, allowedTypes || [], file.name);
            const dataUrl = yield blobToDataUrl(file);
            setPreviewDetails({
                isValid: isAllowed,
                type: file.type.startsWith('image') ? 'image' : file.type.startsWith('video') ? 'video' : 'other',
                mime: file.type,
                dataUrl: dataUrl,
                name: file.name
            });
        });
    }
    function renderPreview() {
        return (_jsx("div", { className: "uxpcore_fileinput__preview-container", children: previewDetails === null ? (_jsxs("div", { className: "uxpcore_fileinput__preview uxpcore_fileinput__preview--empty", children: [_jsx("div", { className: "uxpcore_fileinput__icon", children: _jsx(FontAwesomeIconWrapper, { icon: dropAreaIcon ? dropAreaIcon : ['fas', 'images'] }) }), _jsx("div", { className: "uxpcore_fileinput__text", children: dropAreaLabel ? dropAreaLabel : context.$L('uxp-core.text.drag-n-drop') })] })) : (_jsx("div", { className: "uxpcore_fileinput__preview", children: previewDetails.isValid === false ? (_jsxs("div", { className: "uxpcore_fileinput__invalid", children: [_jsx("div", { className: "uxpcore_fileinput__icon", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times-circle'] }) }), _jsx("div", { className: "uxpcore_fileinput__text", children: context.$L('uxp-core.form-error.invalid-file') })] })) : (_jsx(_Fragment, { children: previewDetails.type === 'image' ? (_jsx("img", { src: previewDetails.dataUrl, className: "uxpcore_fileinput__image", alt: previewDetails.name })) : previewDetails.type === 'video' ? (_jsx("video", { src: previewDetails.dataUrl, className: "uxpcore_fileinput__video" })) : (_jsxs("div", { className: "uxpcore_fileinput__file", children: [_jsx("div", { className: "uxpcore_fileinput__icon", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'file-alt'] }) }), _jsx("div", { className: "uxpcore_fileinput__text", children: previewDetails.name })] })) })) })) }));
    }
    function applyFile(file) {
        if (!file) {
            setInternalValidationError(false);
            onChange(null, false);
            return;
        }
        const isAllowed = validateAllowedFileType(file.type, allowedTypes || [], file.name);
        if (!isAllowed) {
            setInternalValidationError(true);
            onChange(null, false);
            return;
        }
        setInternalValidationError(false);
        onChange(file, true);
    }
    function handleFileChange(e) {
        var _a, _b;
        if (disabled || readOnly)
            return;
        const file = (_b = (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null;
        // Reset so selecting the same file again re-fires onChange
        // (component state is driven by the `value` prop, not the input)
        e.target.value = '';
        applyFile(file);
    }
    function handleDragOver(e) {
        if (disabled || readOnly)
            return;
        e.preventDefault();
        e.stopPropagation();
    }
    function handleDragEnter(e) {
        if (disabled || readOnly)
            return;
        e.preventDefault();
        setIsDragging(true);
    }
    function handleDragLeave(e) {
        var _a;
        if (disabled || readOnly)
            return;
        // Only clear when leaving the container itself, not a child element
        if (!((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget))) {
            setIsDragging(false);
        }
    }
    function handleDrop(e) {
        var _a, _b;
        if (disabled || readOnly)
            return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = (_b = (_a = e.dataTransfer.files) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null;
        applyFile(file);
    }
    function handleContainerClick() {
        var _a;
        if (!disabled && !readOnly) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
        }
    }
    function handleKeyDown(e) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleContainerClick();
        }
    }
    function renderCompact() {
        var _a, _b;
        const icon = dropAreaIcon !== null && dropAreaIcon !== void 0 ? dropAreaIcon : ['fal', 'link'];
        const label = (_b = (_a = previewDetails === null || previewDetails === void 0 ? void 0 : previewDetails.name) !== null && _a !== void 0 ? _a : (typeof value === 'string' && value ? value : null)) !== null && _b !== void 0 ? _b : (dropAreaLabel !== null && dropAreaLabel !== void 0 ? dropAreaLabel : context.$L('uxp-core.text.drag-n-drop'));
        return (_jsxs(_Fragment, { children: [_jsx("input", { type: "file", className: "uxpcore_fileinput__input", ref: inputRef, accept: acceptAttr, onChange: handleFileChange, disabled: disabled, readOnly: readOnly, tabIndex: -1, style: { display: 'none' } }), _jsx("div", { className: "uxpcore_input__prefix uxpcore_input__prefix--separator", children: _jsx("div", { className: "uxpcore_input__slot-content", children: _jsx(FontAwesomeIconWrapper, { icon: icon }) }) }), _jsx("div", { className: classNames('uxpcore_fileinput__compact-label', {
                        'uxpcore_fileinput__compact-label--placeholder': !previewDetails,
                    }), children: label }), previewDetails && !disabled && !readOnly && (_jsx("button", { className: "uxpcore_input__clear-btn", onClick: (e) => { e.stopPropagation(); setPreviewDetails(null); onChange(null, false); }, tabIndex: -1, children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'times'] }) }))] }));
    }
    function handleFocus() {
        if (!disabled && !readOnly) {
            setIsFocused(true);
        }
    }
    function handleBlur() {
        if (!disabled && !readOnly) {
            setIsFocused(false);
        }
    }
    const containerStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined });
    // Parent's isValid prop takes priority.
    // Without it: show invalid when a file was rejected, valid when a file is accepted,
    // and neutral (undefined) when nothing is selected yet.
    const effectiveValidation = isValidProp !== undefined
        ? isValidProp
        : internalValidationError
            ? false
            : previewDetails !== null
                ? true
                : undefined;
    const stateClass = getInputStateClass({ disabled, readOnly, isValid: effectiveValidation });
    // Headless mode: only the (invisible) native input; the consumer supplies
    // the trigger UI and calls open() on the instance ref.
    if (hidden) {
        return (_jsx("input", { type: "file", className: "uxpcore_fileinput__input", ref: inputRef, accept: acceptAttr, onChange: handleFileChange, disabled: disabled, readOnly: readOnly, tabIndex: -1, style: { display: 'none' } }));
    }
    return (_jsx("div", { ref: containerRef, className: classNames("uxpcore_input", "uxpcore_fileinput", className, stateClass, {
            "uxpcore_input--full-width": fullWidth,
            "uxpcore_input--focus": isFocused,
            "uxpcore_input--has-content": !!previewDetails,
            "uxpcore_fileinput--hovered": isHovered && !disabled && !readOnly,
            "uxpcore_fileinput--dragging": isDragging,
            "uxpcore_fileinput--compact": compact,
            "uxpcore_input--has-prefix": compact,
        }), style: containerStyles, onClick: handleContainerClick, onKeyDown: handleKeyDown, onFocus: handleFocus, onBlur: handleBlur, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), onDragOver: handleDragOver, onDragEnter: handleDragEnter, onDragLeave: handleDragLeave, onDrop: handleDrop, tabIndex: disabled || readOnly ? -1 : 0, children: compact ? renderCompact() : (_jsxs(_Fragment, { children: [_jsx("input", { type: "file", className: "uxpcore_fileinput__input", ref: inputRef, accept: acceptAttr, onChange: handleFileChange, disabled: disabled, readOnly: readOnly, tabIndex: -1, style: { display: 'none' } }), _jsxs("div", { className: "uxpcore_fileinput__content", children: [renderPreview(), isHovered && previewDetails && (_jsx("div", { className: "uxpcore_fileinput__hover-overlay", children: _jsx("div", { className: "uxpcore_fileinput__hover-text", children: context.$L('uxp-core.text.drag-n-drop') }) }))] })] })) }));
});
FileInput.displayName = 'FileInput';
export default FileInput;
